import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { PM_SHELL_PROFILE_MENU_HOST, type PmShellProfileMenuHost } from './pm-shell-profile-menu-host';
import { Router } from '@angular/router';
import { toast } from '../../../shared/components/pm-toast/pm-toast';
import { PmUserContextService } from '../../../core/services/pm-user-context.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PmOneLoginTokenService } from '../../auth/pm-onelogin-token.service';

/** Profile menu popup (top bar avatar): the signed-in user's name and firm, My profile, Settings and Sign out. */
@Component({
  selector: 'pm-shell-profile-menu',
  standalone: true,
  imports: [],
  templateUrl: './pm-shell-profile-menu.component.html',
  styleUrl: './pm-shell-profile-menu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PmShellProfileMenuComponent {
  /** Public for template bindings (`practiceContextReady`, `practiceContextLoading`, …). */
  readonly userContext = inject(PmUserContextService);

  private readonly router = inject(Router);


  /** What this section still reads from the workspace (see pm-shell-profile-menu-host.ts). */
  private readonly workspace = inject(PM_SHELL_PROFILE_MENU_HOST);

  get isProfileMenuOpen() {
    return this.workspace.isProfileMenuOpen;
  }

  get profile() {
    return this.workspace.profile;
  }

  stopProp(...args: Parameters<PmShellProfileMenuHost['stopProp']>): ReturnType<PmShellProfileMenuHost['stopProp']> {
    return this.workspace.stopProp(...args);
  }

  // OidcSecurityService only has a provider when provideAuth() runs (environment.oneLoginAuthority/
  // oneLoginClientId set — Dev only today); optional so this component doesn't break in Beta/Prod,
  // where OneLogin isn't configured yet.
  private readonly oidcSecurityService = inject(OidcSecurityService, { optional: true });

  private readonly oneLoginTokenService = inject(PmOneLoginTokenService);

  goToProfilePage(): void {
    this.isProfileMenuOpen.set(false);
    void this.router.navigateByUrl('/profile');
  }

  readonly signingOut = signal(false);

  signOut(): void {
    if (this.signingOut()) {
      return;
    }
    this.isProfileMenuOpen.set(false);
    this.oneLoginTokenService.clear();
    this.userContext.clearSessionAndReset();

    if (!this.oidcSecurityService) {
      toast.info('Sign out is not available yet in this environment.');
      return;
    }

    this.signingOut.set(true);
    this.oidcSecurityService.logoff().subscribe({
      error: () => {
        toast.error('Sign out failed. Please try again.');
        this.signingOut.set(false);
      }
      // No success handler resets signingOut — a successful logoff navigates the browser away
      // to OneLogin, so this component is torn down before there's anything left to reset.
    });
  }
}
